import { Component } from '@theme/component';
import { sectionRenderer } from '@theme/section-renderer';
import { requestIdleCallback, viewTransition, yieldToMainThread } from '@theme/utilities';
import { PaginatedListAspectRatioHelper } from '@theme/paginated-list-aspect-ratio';
import { StandardEvents } from '@shopify/events';
import { getScrollTop, scrollTo } from '@theme/scroll-container';

/**
 * A custom element that renders a paginated list of items.
 *
 * @typedef {object} Refs
 * @property {HTMLUListElement} [grid] - The grid element.
 * @property {HTMLSpanElement} [viewMorePrevious] - The view more previous button.
 * @property {HTMLSpanElement} [viewMoreNext] - The view more next button.
 * @property {HTMLButtonElement} [viewMoreButton] - The "load more" button used in button pagination mode.
 * @property {HTMLElement} [loadMoreStatus] - The "Showing X of Y products" status text next to the load-more button.
 * @property {HTMLElement[]} [cards] - The cards elements.
 *
 * @extends Component<Refs>
 */
export default class PaginatedList extends Component {
  /**
   * @type {Map<number, string>}
   */
  pages = new Map();

  /** @type {IntersectionObserver | undefined} */
  infinityScrollObserver;

  /** @type {((value: void) => void) | null} */
  #resolveNextPagePromise = null;

  /** @type {((value: void) => void) | null} */
  #resolvePreviousPagePromise = null;

  /** @type {PaginatedListAspectRatioHelper} */
  #aspectRatioHelper;

  /** @type {HTMLElement[]} Cards hidden to keep the grid trimmed to full rows in button mode. */
  #trimmedCards = [];

  /** @type {(() => void) | null} */
  #resizeHandler = null;

  /** @type {ReturnType<typeof setTimeout> | null} */
  #resizeTimeout = null;

  /** @type {number | null} */
  #columnCount = null;

  connectedCallback() {
    super.connectedCallback();

    /** @type {HTMLElement | null} */
    const templateCard = this.querySelector('[ref="cardGallery"]');
    if (templateCard) {
      this.#aspectRatioHelper = new PaginatedListAspectRatioHelper({
        templateCard,
      });
    }

    // In button mode, pages are only fetched on demand (via the load-more button), not
    // eagerly on connect like the default infinite-scroll behavior.
    if (!this.#isButtonMode()) {
      this.#fetchPage('next');
      this.#fetchPage('previous');
    }
    this.#observeViewMore();
    this.#bindLoadMoreButton();
    this.#initRowTrimming();

    // Listen for filter updates to clear cached pages
    document.addEventListener(StandardEvents.searchUpdate, this.#handleFilterUpdate);
    document.addEventListener(StandardEvents.collectionUpdate, this.#handleFilterUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.infinityScrollObserver) {
      this.infinityScrollObserver.disconnect();
    }
    const { viewMoreButton } = this.refs;
    if (viewMoreButton) {
      viewMoreButton.removeEventListener('click', this.#handleLoadMoreClick);
    }
    if (this.#resizeHandler) {
      window.removeEventListener('resize', this.#resizeHandler);
      this.#resizeHandler = null;
    }
    if (this.#resizeTimeout) {
      clearTimeout(this.#resizeTimeout);
    }
    // Remove the filter update listeners
    document.removeEventListener(StandardEvents.searchUpdate, this.#handleFilterUpdate);
    document.removeEventListener(StandardEvents.collectionUpdate, this.#handleFilterUpdate);
  }

  /**
   * Bind the click handler for the "load more" button used in button pagination mode.
   */
  #bindLoadMoreButton() {
    const { viewMoreButton } = this.refs;
    if (!viewMoreButton) return;
    viewMoreButton.addEventListener('click', this.#handleLoadMoreClick);
  }

  /**
   * Whether this grid is configured for button (click-to-load-more) pagination
   * rather than the default infinite-scroll behavior.
   * @returns {boolean}
   */
  #isButtonMode() {
    const { grid } = this.refs;
    return grid?.dataset.paginationMode === 'button';
  }

  /**
   * In button mode, trim the grid to whole rows on load and re-trim on resize
   * (column count can change at different breakpoints).
   */
  #initRowTrimming() {
    if (!this.#isButtonMode()) return;
    requestIdleCallback(() => this.#trimGridToFullRows());
    this.#resizeHandler = () => {
      if (this.#resizeTimeout) clearTimeout(this.#resizeTimeout);
      this.#resizeTimeout = setTimeout(() => {
        this.#columnCount = null;
        this.#revealTrimmedCards();
        this.#trimGridToFullRows();
      }, 150);
    };
    window.addEventListener('resize', this.#resizeHandler);
  }

  /**
   * @returns {HTMLElement[]}
   */
  #getVisibleCards() {
    const { grid } = this.refs;
    if (!grid) return [];
    return Array.from(grid.children).filter((el) => el.matches('[ref="cards[]"]') && !el.hidden);
  }

  /**
   * @param {HTMLElement[]} cards
   * @returns {number}
   */
  #getColumnCount(cards) {
    if (!cards.length) return 0;
    const firstTop = cards[0].offsetTop;
    let count = 0;
    for (const card of cards) {
      if (Math.abs(card.offsetTop - firstTop) < 2) {
        count++;
      } else {
        break;
      }
    }
    return count;
  }

  #revealTrimmedCards() {
    if (!this.#trimmedCards.length) return;
    for (const card of this.#trimmedCards) {
      card.hidden = false;
    }
    this.#trimmedCards = [];
  }

  /**
   * Hide any cards in the last, incomplete row so the grid always ends on a full row
   * while more items are pending (button mode only).
   */
  #trimGridToFullRows() {
    if (!this.#isButtonMode()) return;
    const nextPage = this.#getPage('next');
    if (!this.#shouldUsePage(nextPage)) return;

    const cards = this.#getVisibleCards();
    if (!cards.length) return;

    if (this.#columnCount === null) {
      this.#columnCount = this.#getColumnCount(cards);
    }
    const columns = this.#columnCount;
    if (columns < 1) return;

    const remainder = cards.length % columns;
    if (remainder === 0) return;

    const toHide = cards.slice(cards.length - remainder);
    for (const card of toHide) {
      card.hidden = true;
    }
    this.#trimmedCards.push(...toHide);
  }

  /**
   * @param {MouseEvent} event
   */
  #handleLoadMoreClick = async (event) => {
    const button = /** @type {HTMLButtonElement} */ (event.currentTarget);
    if (button.hasAttribute('disabled')) return;

    button.setAttribute('disabled', '');
    button.setAttribute('aria-busy', 'true');

    this.#revealTrimmedCards();
    await this.#renderNextPage();
    this.#trimGridToFullRows();

    const nextPage = this.#getPage('next');
    if (!nextPage || !this.#shouldUsePage(nextPage)) {
      button.closest('.load-more-button-wrapper')?.remove();
      return;
    }

    button.removeAttribute('disabled');
    button.removeAttribute('aria-busy');
  };

  #observeViewMore() {
    const { viewMorePrevious, viewMoreNext } = this.refs;

    // Return if neither element exists
    if (!viewMorePrevious && !viewMoreNext) return;

    // Create observer if it doesn't exist
    if (!this.infinityScrollObserver) {
      this.infinityScrollObserver = new IntersectionObserver(
        async (entries) => {
          // Wait for any in-progress view transitions to finish
          if (viewTransition.current) await viewTransition.current;

          for (const entry of entries) {
            if (entry.isIntersecting) {
              // Use current refs to check which element triggered
              const { viewMorePrevious, viewMoreNext } = this.refs;

              if (entry.target === viewMorePrevious) {
                this.#renderPreviousPage();
              } else if (entry.target === viewMoreNext) {
                this.#renderNextPage();
              }
            }
          }
        },
        {
          rootMargin: '100px',
        }
      );
    }

    // Observe the view more elements
    if (viewMorePrevious) {
      this.infinityScrollObserver.observe(viewMorePrevious);
    }

    if (viewMoreNext) {
      this.infinityScrollObserver.observe(viewMoreNext);
    }
  }

  /**
   * @param {{ page: number, url?: URL } | undefined} pageInfo - The page info
   * @returns {boolean} Whether to use the page
   */
  #shouldUsePage(pageInfo) {
    if (!pageInfo) return false;

    const { grid } = this.refs;
    const lastPage = grid?.dataset.lastPage;

    if (!lastPage || pageInfo.page < 1 || pageInfo.page > Number(lastPage)) return false;

    return true;
  }

  /**
   * @param {"previous" | "next"} type
   */
  async #fetchPage(type) {
    const page = this.#getPage(type);

    // Always resolve the promise, even if we can't fetch the page
    const resolvePromise = () => {
      if (type === 'next') {
        this.#resolveNextPagePromise?.();
        this.#resolveNextPagePromise = null;
      } else {
        this.#resolvePreviousPagePromise?.();
        this.#resolvePreviousPagePromise = null;
      }
    };

    if (!page || !this.#shouldUsePage(page)) {
      // Resolve the promise even if we can't fetch
      resolvePromise();
      return;
    }

    await this.#fetchSpecificPage(page.page, page.url);
    resolvePromise();
  }

  /**
   * @param {number} pageNumber - The page number to fetch
   * @param {URL} [url] - Optional URL, will be constructed if not provided
   */
  async #fetchSpecificPage(pageNumber, url = undefined) {
    const pageInfo = { page: pageNumber, url };

    if (!url) {
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.set('page', pageNumber.toString());
      newUrl.hash = '';
      pageInfo.url = newUrl;
    }

    if (!this.#shouldUsePage(pageInfo)) return;
    const pageContent = await sectionRenderer.getSectionHTML(this.sectionId, true, pageInfo.url);
    this.pages.set(pageNumber, pageContent);
  }

  async #renderNextPage() {
    const { grid } = this.refs;

    if (!grid) return;

    const nextPage = this.#getPage('next');

    if (!nextPage || !this.#shouldUsePage(nextPage)) return;
    let nextPageItemElements = this.#getGridForPage(nextPage.page);

    if (!nextPageItemElements) {
      const promise = new Promise((res) => {
        this.#resolveNextPagePromise = res;
      });

      // Trigger the fetch for this page
      this.#fetchPage('next');

      await promise;
      nextPageItemElements = this.#getGridForPage(nextPage.page);
      if (!nextPageItemElements) return;
    }

    grid.append(...nextPageItemElements);

    this.#aspectRatioHelper.processNewElements();
    this.#updateLoadMoreStatus();

    await yieldToMainThread();

    history.pushState('', '', nextPage.url.toString());

    // In button mode, don't eagerly prefetch the following page — wait for the next click.
    if (!this.#isButtonMode()) {
      requestIdleCallback(() => {
        this.#fetchPage('next');
      });
    }
  }

  async #renderPreviousPage() {
    const { grid } = this.refs;

    if (!grid) return;

    const previousPage = this.#getPage('previous');
    if (!previousPage || !this.#shouldUsePage(previousPage)) return;

    let previousPageItemElements = this.#getGridForPage(previousPage.page);
    if (!previousPageItemElements) {
      const promise = new Promise((res) => {
        this.#resolvePreviousPagePromise = res;
      });

      // Trigger the fetch for this page
      this.#fetchPage('previous');

      await promise;
      previousPageItemElements = this.#getGridForPage(previousPage.page);
      if (!previousPageItemElements) return;
    }

    // Store the current scroll position and height of the first element
    const currentScrollTop = getScrollTop();
    const firstElement = grid.firstElementChild;
    const oldHeight = firstElement ? firstElement.getBoundingClientRect().top + currentScrollTop : 0;

    // Prepend the new elements
    grid.prepend(...previousPageItemElements);

    this.#aspectRatioHelper.processNewElements();
    this.#updateLoadMoreStatus();

    // Calculate and adjust scroll position to maintain the same view
    if (firstElement) {
      const newHeight = firstElement.getBoundingClientRect().top + getScrollTop();
      const heightDiff = newHeight - oldHeight;
      scrollTo({
        top: currentScrollTop + heightDiff,
        behavior: 'instant',
      });
    }

    await yieldToMainThread();

    history.pushState('', '', previousPage.url.toString());

    requestIdleCallback(() => {
      this.#fetchPage('previous');
    });
  }

  /**
   * Updates the "Showing X of Y products" status text next to the load-more button, if present.
   * Queries the grid directly rather than using `this.refs.cards` — the ref list doesn't pick up
   * elements appended via `grid.append()` in the same tick, so it undercounts immediately after
   * a page loads. A direct query is always accurate. Includes rows hidden by row-trimming, since
   * those are still loaded, just not currently visible.
   */
  #updateLoadMoreStatus() {
    const { loadMoreStatus, grid } = this.refs;
    if (!loadMoreStatus || !grid) return;

    const total = grid.dataset.totalItems;
    if (!total) return;

    const loadedCount = grid.querySelectorAll(':scope > [ref="cards[]"]').length;
    loadMoreStatus.textContent = `Showing ${loadedCount} of ${total} products`;
  }

  /**
   * @param {"previous" | "next"} type
   * @returns {{ page: number, url: URL } | undefined}
   */
  #getPage(type) {
    const { cards } = this.refs;
    const isPrevious = type === 'previous';

    if (!Array.isArray(cards)) return;

    const targetCard = cards[isPrevious ? 0 : cards.length - 1];

    if (!targetCard) return;

    const currentCardPage = Number(targetCard.dataset.page);
    const page = isPrevious ? currentCardPage - 1 : currentCardPage + 1;

    const url = new URL(window.location.href);
    url.searchParams.set('page', page.toString());
    url.hash = '';

    return {
      page,
      url,
    };
  }

  /**
   * @param {number} page
   * @returns {NodeListOf<Element> | undefined}
   */
  #getGridForPage(page) {
    const pageHTML = this.pages.get(page);

    if (!pageHTML) return;

    const parsedPage = new DOMParser().parseFromString(pageHTML, 'text/html');
    const gridElement = parsedPage.querySelector('[ref="grid"]');
    if (!gridElement) return;
    return gridElement.querySelectorAll(':scope > [ref="cards[]"]');
  }

  get sectionId() {
    const id = this.getAttribute('section-id');

    if (!id) throw new Error('The section-id attribute is required');

    return id;
  }

  /**
   * Handle filter updates by clearing cached pages.
   * Only reacts to events from the same section (ignores e.g. predictive search in the header).
   * @param {Event} event
   */
  #handleFilterUpdate = (event) => {
    const eventSection = /** @type {Element | null} */ (event.target)?.closest('[id^="shopify-section-"]');
    if (eventSection && eventSection.id !== `shopify-section-${this.sectionId}`) return;
    this.pages.clear();

    // Resolve any pending promises to unblock waiting renders
    this.#resolveNextPagePromise?.();
    this.#resolvePreviousPagePromise?.();

    this.#resolveNextPagePromise = null;
    this.#resolvePreviousPagePromise = null;
    this.#trimmedCards = [];
    this.#columnCount = null;

    // Store the current lastPage value to detect when it changes
    const currentLastPage = this.refs.grid?.dataset.lastPage;

    // We need to wait for the DOM to be updated with the new filtered content
    // Using mutation observer to detect when the grid actually updates
    const observer = new MutationObserver(() => {
      // Check if data-last-page changed
      const newLastPage = this.refs.grid?.dataset.lastPage;

      if (newLastPage !== currentLastPage) {
        observer.disconnect();

        // Check if component is still connected
        if (!this.isConnected) {
          return;
        }

        // Now the DOM has been updated with the new filtered content
        this.#observeViewMore();
        this.#bindLoadMoreButton();

        // Fetch the next page (unless in button mode, which waits for a click)
        if (!this.#isButtonMode()) {
          this.#fetchPage('next');
        }

        requestIdleCallback(() => this.#trimGridToFullRows());
      }
    });

    // Observe the grid for changes
    const { grid } = this.refs;
    if (grid) {
      observer.observe(grid, {
        attributes: true,
        attributeFilter: ['data-last-page'],
        childList: true, // Also watch for child changes in case the whole grid is replaced
      });

      // Set a timeout as a fallback in case the mutation never fires
      setTimeout(() => {
        if (observer) {
          observer.disconnect();
        }
      }, 3000);
    }
  };
}
